import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API } from '../utils/api';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import { Popover, PopoverTrigger, PopoverContent } from '../components/ui/popover';

const createDotIcon = (color) => L.divIcon({
  className: 'custom-marker',
  html: `<div style="background:${color};width:22px;height:22px;border-radius:50%;border:2px solid white;box-shadow:0 2px 5px rgba(0,0,0,0.3);"></div>`,
  iconSize: [22, 22], iconAnchor: [11, 22], popupAnchor: [0, -22],
});

/** The Cockpit Dashboard — embeds search, map, stats, analytics below chat */
const CockpitDashboard = ({ showToast }) => {
  const { token, user } = useAuth();
  const [activeTab, setActiveTab] = useState('search');
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  
  // Map state
  const [mapResults, setMapResults] = useState([]);
  const [mapDataSource, setMapDataSource] = useState('personal');
  const [refreshingDots, setRefreshingDots] = useState(false);
  const [refreshProgress, setRefreshProgress] = useState('');
  
  // Stats state
  const [stats, setStats] = useState(null);
  
  // Analytics state
  const [analytics, setAnalytics] = useState(null);
  
  // Duplicates state
  const [duplicates, setDuplicates] = useState([]);
  const [dupLoading, setDupLoading] = useState(false);

  const xhr = useCallback((method, url, body) => {
    return new Promise((resolve, reject) => {
      const x = new XMLHttpRequest();
      x.open(method, `${API}${url}`, true);
      x.setRequestHeader('Content-Type', 'application/json');
      if (token) x.setRequestHeader('Authorization', `Bearer ${token}`);
      x.onload = () => { try { resolve(JSON.parse(x.responseText)); } catch { resolve({}); } };
      x.onerror = reject;
      x.send(body ? JSON.stringify(body) : null);
    });
  }, [token]);

  // Semantic search
  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearchLoading(true);
    try {
      const res = await xhr('POST', '/search/semantic', { query: searchQuery, size: 15 });
      setSearchResults(res.results || []);
      showToast?.(`Found ${res.total || 0} results`, 'success');
    } catch { showToast?.('Search failed', 'error'); }
    setSearchLoading(false);
  };

  // Fetch map data
  const fetchMap = useCallback(async () => {
    const personal = mapDataSource === 'personal';
    const res = await xhr('GET', `/map/worldwide?personal=${personal}&limit=500`, null);
    setMapResults(res.results || []);
  }, [xhr, mapDataSource]);

  useEffect(() => { if (token && activeTab === 'map') fetchMap(); }, [token, activeTab, fetchMap]);

  // Refresh dots
  const refreshMapDots = async () => {
    setRefreshingDots(true);
    setRefreshProgress('Starting...');
    let total = 0, calls = 0;
    while (calls < 50) {
      calls++;
      try {
        const res = await xhr('POST', '/map/refresh-dots', {});
        total += (res.text_geocoded || 0) + (res.ip_geocoded || 0);
        setRefreshProgress(`Batch ${calls}: ${res.remaining} remaining...`);
        if (res.remaining === 0 || res.processed === 0) break;
      } catch { break; }
    }
    setRefreshProgress(`Done! ${total} URLs geocoded.`);
    showToast?.(`Refreshed ${total} dots`, 'success');
    fetchMap();
    setRefreshingDots(false);
  };

  // Fetch stats
  useEffect(() => {
    if (token && activeTab === 'stats') {
      xhr('GET', '/map-data/statistics', null).then(setStats);
    }
  }, [token, activeTab, xhr]);

  // Fetch analytics
  useEffect(() => {
    if (token && activeTab === 'analytics') {
      xhr('GET', '/dashboard-init', null).then(setAnalytics);
    }
  }, [token, activeTab, xhr]);

  // Fetch duplicates
  const loadDuplicates = async () => {
    setDupLoading(true);
    const res = await xhr('GET', '/search/duplicates', null);
    setDuplicates(res.duplicates || []);
    setDupLoading(false);
  };

  const mapCenter = useMemo(() => {
    const pts = mapResults.filter(r => r.latitude && r.longitude);
    if (pts.length === 0) return [20, 0];
    return [pts.reduce((s, r) => s + r.latitude, 0) / pts.length, pts.reduce((s, r) => s + r.longitude, 0) / pts.length];
  }, [mapResults]);

  const tabs = [
    { id: 'search', label: 'Search', icon: 'fa-search' },
    { id: 'map', label: 'Map', icon: 'fa-map-marker-alt' },
    { id: 'stats', label: 'Statistics', icon: 'fa-chart-bar' },
    { id: 'analytics', label: 'Analytics', icon: 'fa-chart-pie' },
    { id: 'duplicates', label: 'Duplicates', icon: 'fa-clone' },
  ];

  return (
    <div data-testid="cockpit-dashboard" style={{ marginTop: 16, border: '1px solid rgba(253, 224, 71,0.15)', borderRadius: 12, overflow: 'hidden', background: 'rgba(10,5,25,0.6)' }}>
      {/* Tab bar */}
      <div style={{ display: 'flex', borderBottom: '1px solid rgba(255,255,255,0.06)', overflowX: 'auto' }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)} data-testid={`cockpit-tab-${t.id}`}
            style={{
              padding: '10px 18px', border: 'none', cursor: 'pointer',
              background: activeTab === t.id ? 'rgba(253, 224, 71,0.15)' : 'transparent',
              color: activeTab === t.id ? '#047857' : '#71717a',
              fontSize: '0.8rem', fontWeight: activeTab === t.id ? 700 : 400,
              borderBottom: activeTab === t.id ? '2px solid #FCD34D' : '2px solid transparent',
              whiteSpace: 'nowrap'
            }}>
            <i className={`fas ${t.icon}`} style={{ marginRight: 6 }} />{t.label}
          </button>
        ))}
      </div>

      <div style={{ padding: 14, minHeight: 300 }}>
        {/* SEARCH TAB */}
        {activeTab === 'search' && (
          <div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSearch()}
                placeholder="Semantic search across your data..."
                data-testid="cockpit-search-input"
                style={{ flex: 1, background: 'rgba(253, 251, 247,0.6)', border: '1px solid rgba(6,182,212,0.3)', borderRadius: 8, padding: '8px 14px', color: '#e4e4e7', fontSize: '0.85rem' }} />
              <button onClick={handleSearch} disabled={searchLoading} data-testid="cockpit-search-btn"
                style={{ background: 'linear-gradient(135deg, #0891b2, #FCD34D)', border: 'none', color: '#0F172A', padding: '8px 18px', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>
                {searchLoading ? 'Searching...' : 'Search'}
              </button>
            </div>
            <div style={{ maxHeight: 350, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
              {searchResults.map((r, i) => (
                <div key={r.id || i} data-testid={`cockpit-result-${i}`} style={{
                  background: 'rgba(253, 251, 247,0.4)', border: '1px solid rgba(6,182,212,0.12)', borderRadius: 8, padding: '10px 14px'
                }}>
                  <a href={r.url} target="_blank" rel="noopener noreferrer"
                    style={{ color: '#22d3ee', fontWeight: 600, fontSize: '0.83rem', textDecoration: 'none' }}
                    dangerouslySetInnerHTML={{ __html: r.highlight_title || r.title || 'Untitled' }} />
                  <div style={{ color: '#a1a1aa', fontSize: '0.72rem', marginTop: 4 }}
                    dangerouslySetInnerHTML={{ __html: r.highlight_snippet || r.snippet || '' }} />
                  <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                    <span style={{ fontSize: '0.62rem', color: '#71717a', background: 'rgba(255, 255, 255, 0.85)', padding: '1px 6px', borderRadius: 4 }}>{r.root_domain}</span>
                    <span style={{ fontSize: '0.62rem', color: '#f59e0b', background: 'rgba(245,158,11,0.1)', padding: '1px 6px', borderRadius: 4 }}>{r.score?.toFixed(1)}</span>
                  </div>
                </div>
              ))}
              {searchResults.length === 0 && !searchLoading && (
                <p style={{ color: '#555', textAlign: 'center', padding: 30, fontSize: '0.85rem' }}>
                  Search your indexed data using Elasticsearch semantic search.
                </p>
              )}
            </div>
          </div>
        )}

        {/* MAP TAB */}
        {activeTab === 'map' && (
          <div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', borderRadius: 8, overflow: 'hidden', border: '1px solid rgba(253, 224, 71,0.3)' }}>
                {['personal', 'worldwide'].map(ds => (
                  <button key={ds} onClick={() => setMapDataSource(ds)} data-testid={`cockpit-map-${ds}`}
                    style={{
                      padding: '6px 14px', border: 'none', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600,
                      background: mapDataSource === ds ? 'rgba(253, 224, 71,0.3)' : 'transparent',
                      color: mapDataSource === ds ? '#047857' : '#71717a'
                    }}>
                    {ds === 'personal' ? 'My Data' : 'Worldwide'}
                  </button>
                ))}
              </div>
              <button onClick={refreshMapDots} disabled={refreshingDots} data-testid="cockpit-refresh-dots-btn"
                style={{ background: 'rgba(16,185,129,0.2)', border: '1px solid rgba(16,185,129,0.4)', color: '#10b981', padding: '6px 12px', borderRadius: 8, cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600 }}>
                {refreshingDots ? 'Refreshing...' : 'Refresh Map Dots'}
              </button>
              <span style={{ color: '#10b981', fontSize: '0.72rem', fontWeight: 600 }}>
                {mapResults.filter(r => r.latitude && r.longitude).length} dots
              </span>
              {refreshProgress && <span style={{ color: '#a1a1aa', fontSize: '0.65rem' }}>{refreshProgress}</span>}
            </div>
            <div style={{ height: 350, borderRadius: 8, overflow: 'hidden' }}>
              <MapContainer center={mapCenter} zoom={2} style={{ height: '100%', width: '100%' }} scrollWheelZoom={true}>
                <TileLayer url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png" />
                {mapResults.filter(r => r.latitude && r.longitude).map((r, i) => (
                  <Marker key={i} position={[r.latitude, r.longitude]}
                    icon={createDotIcon(r.categories?.length > 1 ? '#f59e0b' : '#FCD34D')}>
                    <Popup>
                      <div style={{ maxWidth: 250 }}>
                        <strong>{r.title || 'Untitled'}</strong>
                        <br /><a href={r.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.7rem' }}>{r.url?.slice(0, 60)}</a>
                        {r.categories && <div style={{ fontSize: '0.65rem', color: '#666', marginTop: 4 }}>{r.categories.join(', ')}</div>}
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
          </div>
        )}

        {/* STATISTICS TAB */}
        {activeTab === 'stats' && (
          <div>
            {stats ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10 }}>
                {[
                  { label: 'Total URLs', value: stats.total_results || 0, color: '#3b82f6' },
                  { label: 'Categories', value: stats.total_categories || 0, color: '#047857' },
                  { label: 'Mapped', value: stats.mapped_results || 0, color: '#10b981' },
                  { label: 'Countries', value: stats.unique_countries || 0, color: '#f59e0b' },
                  { label: 'Doc Types', value: stats.document_types?.length || 0, color: '#047857' },
                  { label: 'Avg Quality', value: (stats.avg_quality_score || 0).toFixed(1), color: '#06b6d4' },
                ].map((s, i) => (
                  <div key={i} style={{ background: `${s.color}15`, border: `1px solid ${s.color}30`, borderRadius: 10, padding: 14, textAlign: 'center' }}>
                    <div style={{ color: s.color, fontSize: '1.6rem', fontWeight: 700 }}>{s.value}</div>
                    <div style={{ color: '#71717a', fontSize: '0.72rem', marginTop: 2 }}>{s.label}</div>
                  </div>
                ))}
                {stats.top_domains && stats.top_domains.length > 0 && (
                  <div style={{ gridColumn: '1 / -1', background: 'rgba(245, 158, 11,0.06)', border: '1px solid rgba(245, 158, 11,0.15)', borderRadius: 10, padding: 14 }}>
                    <h4 style={{ color: '#60a5fa', margin: '0 0 8px', fontSize: '0.85rem' }}>Top Domains</h4>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {stats.top_domains.slice(0, 8).map((d, i) => (
                        <span key={i} style={{ fontSize: '0.7rem', color: '#93c5fd', background: 'rgba(245, 158, 11,0.1)', padding: '3px 8px', borderRadius: 12 }}>
                          {d.domain || d._id} ({d.count})
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p style={{ color: '#555', textAlign: 'center', padding: 30 }}>Loading statistics...</p>
            )}
          </div>
        )}

        {/* ANALYTICS TAB */}
        {activeTab === 'analytics' && (
          <div>
            {analytics ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 }}>
                {[
                  { label: 'Total Searches', value: analytics.total_searches || analytics.stats?.total_searches || 0, color: '#3b82f6' },
                  { label: 'URLs Found', value: analytics.total_urls || analytics.stats?.total_urls || 0, color: '#10b981' },
                  { label: 'Categories', value: analytics.total_categories || analytics.stats?.total_categories || 0, color: '#047857' },
                  { label: 'Active Protocols', value: analytics.protocols_count || analytics.stats?.active_protocols || 0, color: '#f59e0b' },
                ].map((s, i) => (
                  <div key={i} style={{ background: `${s.color}15`, border: `1px solid ${s.color}30`, borderRadius: 10, padding: 14, textAlign: 'center' }}>
                    <div style={{ color: s.color, fontSize: '1.8rem', fontWeight: 700 }}>{s.value}</div>
                    <div style={{ color: '#71717a', fontSize: '0.75rem', marginTop: 2 }}>{s.label}</div>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ color: '#555', textAlign: 'center', padding: 30 }}>Loading analytics...</p>
            )}
          </div>
        )}

        {/* DUPLICATES TAB */}
        {activeTab === 'duplicates' && (
          <div>
            <button onClick={loadDuplicates} disabled={dupLoading} data-testid="cockpit-scan-duplicates-btn"
              style={{ marginBottom: 10, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', color: '#f87171', padding: '6px 14px', borderRadius: 8, cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>
              {dupLoading ? 'Scanning...' : 'Scan for Duplicates'}
            </button>
            <div style={{ maxHeight: 320, overflowY: 'auto' }}>
              {duplicates.length === 0 && !dupLoading && (
                <p style={{ color: '#555', textAlign: 'center', padding: 20, fontSize: '0.8rem' }}>
                  Click "Scan for Duplicates" to find duplicate URLs in your database.
                </p>
              )}
              {duplicates.map((d, i) => (
                <div key={i} data-testid={`cockpit-dup-${i}`} style={{
                  padding: '8px 12px', marginBottom: 4, borderRadius: 6, background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.1)'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#fca5a5', fontSize: '0.75rem', fontWeight: 600 }}>{d.normalized_url}</span>
                    <span style={{ color: '#ef4444', fontSize: '0.65rem', background: 'rgba(239,68,68,0.15)', padding: '2px 8px', borderRadius: 10 }}>{d.count}x</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CockpitDashboard;
